/**
 * @file     error.tsx
 * @readme   ../../CLAUDE.md
 * @purpose  App Router error boundary: reports the error, recovers stale chunks and
 *           transient stream drops, and shows SystemPage with Try again and Home page
 */
'use client';

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { HubActionButton } from '@/components/hub/HubActionButton';
import { SystemPage } from '@/views/system/SystemPage';
import { reportError } from '@/services/error-reporting/reportError';
import { isChunkLoadError, recoverFromChunkLoadError } from '@/services/error-reporting/chunkLoadError';
import { isTransientNetworkError } from '@/services/error-reporting/transientNetworkError';

// Window-scoped attempt counter so a transient error in a stuck stream
// (e.g. Safari killed an RSC connection that hasn't reopened) cannot
// retry forever and spam the console.
const TRANSIENT_RESET_KEY = '__relta_transient_reset_attempts__';
const TRANSIENT_RESET_WINDOW_MS = 5000;
const TRANSIENT_RESET_MAX = 2;
function shouldRetryTransient(): boolean {
  if (typeof window === 'undefined') return false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const store = (window as any)[TRANSIENT_RESET_KEY] as { count: number; firstAt: number } | undefined;
  const now = Date.now();
  if (!store || now - store.firstAt > TRANSIENT_RESET_WINDOW_MS) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any)[TRANSIENT_RESET_KEY] = { count: 1, firstAt: now };
    return true;
  }
  if (store.count >= TRANSIENT_RESET_MAX) return false;
  store.count += 1;
  return true;
}

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const { t } = useTranslation();

  // Log the error so we can actually diagnose. The default boundary swallows
  // it entirely, which is why Ray's "something went wrong" page had no
  // useful console output for two debugging cycles. This logs the message,
  // stack, and Next's digest so production crashes are visible in the
  // browser console (and visible in Vercel runtime logs via console.error).
  useEffect(() => {
    // Stale chunk after a new deploy: reload to recover, don't report.
    if (isChunkLoadError(error)) {
      recoverFromChunkLoadError();
      return;
    }
    // Safari aborts in-flight RSC/fetch streams when the tab backgrounds or
    // the user navigates away. Surfaces here with no stack/digest. Try to
    // recover by re-rendering: but cap retries so a stuck stream can't
    // infinite-loop the boundary (saw this in prod 2026-06-04 spamming
    // 'Connection closed' continuously).
    //
    // Also: a stale cached client bundle parsing a fresh server's RSC
    // stream can throw 'Connection closed' as a protocol mismatch. A
    // re-render won't fix that: only a hard reload swaps in the fresh
    // bundle. Try the chunk-recovery reload path FIRST (sessionStorage
    // guarded so it can't loop). If reload was already used this session,
    // fall back to the soft reset retries.
    if (isTransientNetworkError(error)) {
      if (recoverFromChunkLoadError()) return;
      if (shouldRetryTransient()) reset();
      return;
    }
    console.error('[app/error] caught render error:', {
      message: error.message,
      stack: error.stack,
      digest: error.digest,
      name: error.name,
    });
    reportError({
      message: error.message,
      stack: error.stack,
      digest: error.digest,
      boundary: 'app/error',
    });
  }, [error]);

  return (
    <SystemPage
      title={t('error.headline', 'Something went wrong')}
      description={t('error.body', 'This page did not load.')}
      actionSlot={
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2 }}>
          <HubActionButton action={{ label: t('error.retry', 'Try again'), onClick: reset }} />
          <HubActionButton action={{ label: t('error.home', { defaultValue: 'Home page' }), href: '/' }} variant="secondary" />
        </Box>
      }
    />
  );
}
